import type { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/** Decimal numeric string (optional leading minus, optional fractional part). */
const DECIMAL_NUMERIC_RE = /^-?(?:\d+(?:\.\d+)?|\.\d+)$/;

/**
 * Render a finite lat/lng number for form display / API strings without scientific notation
 * or typical IEEE float noise (e.g. -84.42809999999999 → -84.4281).
 */
export function geoNumberToDecimalString(n: number): string {
  if (!Number.isFinite(n)) return '';
  return n.toFixed(8).replace(/\.?0+$/, '');
}

/**
 * Normalize API/card latitude or longitude for text inputs — preserves valid decimal strings
 * and formats numeric values safely (never uses raw `String(number)` float artifacts).
 */
export function formatGeoCoordinateForInput(val: unknown): string {
  if (val == null) return '';
  if (typeof val === 'string') {
    const t = val.trim();
    if (!t) return '';
    if (DECIMAL_NUMERIC_RE.test(t)) {
      return t.startsWith('.') ? `0${t}` : t.startsWith('-.') ? `-0${t.slice(1)}` : t;
    }
    const n = Number(t);
    return Number.isFinite(n) ? geoNumberToDecimalString(n) : t;
  }
  if (typeof val === 'number' && Number.isFinite(val)) {
    return geoNumberToDecimalString(val);
  }
  return String(val).trim();
}

function parseDecimalCoordinate(raw: unknown): number | null | 'empty' {
  const s = formatGeoCoordinateForInput(raw);
  if (s === '') return 'empty';
  if (!DECIMAL_NUMERIC_RE.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function geoCoordinateValidator(
  min: number,
  max: number,
  rangeErrorKey: 'latitudeRange' | 'longitudeRange'
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const parsed = parseDecimalCoordinate(control.value);
    if (parsed === 'empty') return { required: true };
    if (parsed === null) return { coordinateDecimal: true };
    if (parsed < min || parsed > max) return { [rangeErrorKey]: true };
    return null;
  };
}

/** Latitude: required decimal in [-90, 90]. */
export const latitudeRangeValidator = geoCoordinateValidator(-90, 90, 'latitudeRange');

/** Longitude: required decimal in [-180, 180]. */
export const longitudeRangeValidator = geoCoordinateValidator(-180, 180, 'longitudeRange');

/** @deprecated Use {@link latitudeRangeValidator} — kept for imports that expect an array. */
export const latitudeFormValidators = [latitudeRangeValidator] as const;

/** @deprecated Use {@link longitudeRangeValidator} — kept for imports that expect an array. */
export const longitudeFormValidators = [longitudeRangeValidator] as const;

/** Whether to show inline geo validation (field was edited or blurred). */
export function geoCoordinateFieldShowError(control: AbstractControl | null): boolean {
  if (!control) return false;
  return (control.touched || control.dirty) && control.invalid;
}

/** Inline error copy for latitude / longitude fields (Add / Edit airport). */
export function geoCoordinateErrorMessage(
  control: AbstractControl | null,
  kind: 'latitude' | 'longitude'
): string | null {
  if (!control?.invalid) return null;
  if (control.hasError('required')) {
    return kind === 'latitude' ? 'Latitude is required.' : 'Longitude is required.';
  }
  if (control.hasError('coordinateDecimal')) {
    return kind === 'latitude'
      ? 'Latitude must be a decimal numeric value (e.g. 12.9716, -33.8688).'
      : 'Longitude must be a decimal numeric value (e.g. 77.5946, -74.0060).';
  }
  if (control.hasError('latitudeRange')) {
    return 'Latitude must be between -90 and 90.';
  }
  if (control.hasError('longitudeRange')) {
    return 'Longitude must be between -180 and 180.';
  }
  return null;
}
